import { AnimatePresence, motion, useMotionValueEvent, useSpring, useTransform } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useSiteText } from "@/lib/siteContent";
import { useDevice } from "@/lib/useDevice";
import { pickCar, useWashCars, washImg } from "@/lib/washOptions";

export const WASH_SPRING = { stiffness: 70, damping: 22, mass: 0.5, restDelta: 0.0005 };

// Portrait phones get the whole car (contain) instead of a zoomed-in crop — see .wash-img in index.css
const Layer = ({ src, style, car }) => (
  <motion.img src={src} alt="" draggable={false} decoding="async" style={{ willChange: "transform, opacity", ...style }}
    className={`wash-img absolute inset-0 w-full h-full ${car ? "wash-car" : ""}`} />
);

// Scene windows on 0..1 scroll progress
export const SCENES = [[0, 0.2], [0.22, 0.38], [0.4, 0.55], [0.57, 0.71], [0.73, 0.86], [0.88, 1.01]];
const ORIGIN = { x: 58, y: 56 };

const BUBBLES = Array.from({ length: 22 }, (_, i) => {
  const r = (n) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1;
  return { pos: r(1) * 100, off: -10 - r(2) * 150, size: 10 + r(3) * 40, dur: 2.2 + r(4) * 2.6, delay: r(5) * -4 };
});

const FX_CLASS = {
  bubbles: (b) => ["wash-bubble wash-bubble-drift block rounded-full", { width: b.size, height: b.size }],
  sparkles: (b) => ["wash-sparkle block", { width: 14 + b.size, height: 14 + b.size }],
  spray: (b) => ["wash-drop block rounded-full", { width: 4 + b.size / 10, height: 16 + b.size }],
};

// Particles riding the wipe edge (CSS-animated, composited only)
const Particles = ({ horizontal, unskew = 0, effect }) => {
  const [kind, lite] = effect.split(":");
  if (!FX_CLASS[kind]) return null;
  return (lite ? BUBBLES.filter((_, i) => i % 2 === 0) : BUBBLES).map((b, i) => {
    const [cls, size] = FX_CLASS[kind](b);
    return (
      <span key={i} className="absolute" style={{ ...(horizontal ? { left: `${b.pos}%`, top: b.off } : { top: `${b.pos}%`, left: b.off }), transform: unskew ? `skewX(${-unskew}deg)` : undefined }}>
        <span className={cls} style={{ ...size, animationDuration: `${b.dur * (kind === "spray" ? 0.4 : 1)}s`, animationDelay: `${b.delay}s` }} />
      </span>
    );
  });
};

// Soft foam glow drawn with gradients (no CSS blur filters — those repaint every frame)
const FOAM_V = "linear-gradient(to right, transparent, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.4) 92%, rgba(186,230,253,0.9) 100%)";
const FOAM_H = "linear-gradient(to bottom, transparent, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.4) 92%, rgba(186,230,253,0.9) 100%)";

// Foam rides the edge via transform (x / y in % of the full-size wrapper) — no layout work per frame
const LineFoam = ({ t, opacity, horizontal, skew = 0, effect, diagonal }) => {
  const x = useTransform(t, (v) => `${diagonal ? v * 130 - 30 : v * 100}%`);
  const y = useTransform(t, (v) => `${v * 100}%`);
  const band = effect !== "none";
  return horizontal ? (
    <motion.div style={{ y, opacity, willChange: "transform, opacity" }} className="absolute inset-0 pointer-events-none" data-testid="wash-foam">
      {band && <div className="absolute left-0 right-0 -top-28 h-28" style={{ background: FOAM_H }} />}
      <div className="absolute left-0 right-0 top-0 h-[3px] bg-sky-100 shadow-[0_0_24px_6px_rgba(56,189,248,0.55)]" data-testid="wash-edge" />
      <div className="absolute left-0 right-0 top-0 h-0"><Particles horizontal effect={effect} /></div>
    </motion.div>
  ) : (
    <motion.div style={{ x, opacity, willChange: "transform, opacity" }} className="absolute inset-0 pointer-events-none" data-testid="wash-foam">
      <motion.div style={{ skewX: skew }} className="absolute top-0 bottom-0 left-0 w-0 origin-bottom">
        {band && <div className="absolute top-0 bottom-0 -left-28 w-28" style={{ background: FOAM_V }} />}
        <div className="absolute top-0 bottom-0 -left-[1.5px] w-[3px] bg-sky-100 shadow-[0_0_24px_6px_rgba(56,189,248,0.55)]" data-testid="wash-edge" />
        <Particles unskew={skew} effect={effect} />
      </motion.div>
    </motion.div>
  );
};

const RingFoam = ({ t, opacity }) => {
  const scale = useTransform(t, (v) => Math.max(v * v * 2.4, 0.001));
  return (
    <motion.div style={{ scale, opacity, left: `${ORIGIN.x}%`, top: `${ORIGIN.y}%`, willChange: "transform, opacity" }}
      className="absolute w-[100vmax] h-[100vmax] -ml-[50vmax] -mt-[50vmax] rounded-full pointer-events-none border-2 border-sky-200/90 shadow-[0_0_40px_10px_rgba(56,189,248,0.35)]" data-testid="wash-foam" />
  );
};

// Clean layer reveal. Side wipe & squeegee use a counter-translated mask (GPU only);
// spotlight & diagonal need a clip-path.
const CleanReveal = ({ src, style, t }) => {
  const outerX = useTransform(t, (v) => `${(v - 1) * 100}%`);
  const innerX = useTransform(t, (v) => `${(1 - v) * 100}%`);
  const outerY = useTransform(t, (v) => `${(v - 1) * 100}%`);
  const innerY = useTransform(t, (v) => `${(1 - v) * 100}%`);
  const clip = useTransform(t, (v) => (style === "spotlight" ? `circle(${v * v * 120}vmax at ${ORIGIN.x}% ${ORIGIN.y}%)`
    : `polygon(0 0, ${v * 130}% 0, ${v * 130 - 30}% 100%, 0 100%)`));
  if (style === "wipe" || style === "squeegee") {
    const h = style === "wipe";
    return (
      <motion.div className="absolute inset-0 overflow-hidden" style={{ [h ? "x" : "y"]: h ? outerX : outerY, willChange: "transform" }}>
        <Layer src={src} car style={{ [h ? "x" : "y"]: h ? innerX : innerY }} />
      </motion.div>
    );
  }
  return <Layer src={src} car style={{ clipPath: clip }} />;
};

const CAPTION_POS = {
  corner: "absolute right-4 sm:right-16 lg:right-44 top-[76px] sm:top-auto sm:bottom-12 lg:bottom-12 text-right",
  headline: "absolute inset-x-0 top-1/2 -translate-y-1/2 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left",
};
const CAPTION_TEXT = {
  corner: "font-semibold uppercase tracking-[0.12em] text-white/95 text-xl sm:text-4xl lg:text-5xl",
  headline: "font-light text-white text-5xl sm:text-7xl lg:text-8xl tracking-tight border-l-4 border-sky-500 pl-6",
};

const Caption = ({ text, testid, place }) => (
  <div className={`${CAPTION_POS[place]} pointer-events-none`}>
    <motion.p data-testid={testid} aria-label={text} initial="hide" animate="show" exit="out"
      variants={{ show: { transition: { staggerChildren: 0.03 } }, out: { opacity: 0, y: -16, transition: { duration: 0.25 } } }}
      className={`font-['Outfit'] leading-none [text-shadow:0_4px_24px_rgba(0,0,0,0.9)] ${CAPTION_TEXT[place]}`}>
      {[...text].map((ch, k) => (
        <motion.span key={k} className="inline-block" variants={{ hide: { opacity: 0, y: "0.5em" }, show: { opacity: 1, y: "0em", transition: { duration: 0.35, ease: [0.25, 0.4, 0.25, 1] } } }}>
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </motion.p>
  </div>
);

export const useScene = (p) => {
  const [scene, setScene] = useState(0);
  useMotionValueEvent(p, "change", (v) => {
    const i = Math.max(0, SCENES.findIndex(([, b]) => v < b));
    setScene((s) => (s === i ? s : i));
  });
  return scene;
};

export const WashScenes = ({ progress, preview, place = "corner", last = 5 }) => {
  const site = useSiteText();
  const device = useDevice();
  const cars = useWashCars();
  const rawCar = preview?.car || site("wash.car");
  const carsKey = cars.map((c) => c.id).join();
  const car = useMemo(() => pickCar(rawCar, cars), [rawCar, carsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const style = preview?.style || site("wash.style");
  const effect = preview?.effect || site("wash.effect");
  const fx = device.isMobile || preview ? (effect === "none" ? "none" : `${effect}:lite`) : effect;
  const p = useSpring(progress, WASH_SPRING);
  const scene = useScene(p);
  useEffect(() => { [car.dirty, car.clean, ...["swirls", "beads", "hand-key"].map(washImg)].forEach((u) => { const i = new Image(); i.src = u; }); }, [car]);
  const t = useTransform(p, [0.02, 0.2], [0, 1]);
  const skew = -Math.atan((0.3 * (preview ? 16 : device.width)) / Math.max(preview ? 9 : device.height, 1)) * (180 / Math.PI);
  const foamOpacity = useTransform(p, [0, 0.03, 0.18, 0.21], [0, 1, 1, 0]);
  const zoom = useTransform(p, [0.2, 0.38, 0.73, 0.86], [1, 2.2, 1.12, 1]);
  const flash = useTransform(p, [0.73, 0.77, 0.84], [0, 0.18, 0]);
  const swirlOpacity = useTransform(p, [0.38, 0.42, 0.52, 0.57], [0, 1, 1, 0]);
  const swirlRotate = useTransform(p, [0.4, 0.57], [0, 120]);
  const swirlScale = useTransform(p, [0.4, 0.57], [1.15, 1.7]);
  const beadOpacity = useTransform(p, [0.54, 0.58, 0.69, 0.73], [0, 1, 1, 0]);
  const beadScale = useTransform(p, [0.55, 0.73], [1.2, 1]);
  const keysOpacity = useTransform(p, [0.85, 0.9], [0, last === 5 ? 1 : 0]);
  const keysScale = useTransform(p, [0.86, 1], [1.1, 1]);
  const handY = useTransform(p, [0.86, 0.97], ["60%", "8%"]);
  const handRotate = useTransform(p, [0.86, 0.97], [12, -4]);
  const bar = useTransform(p, [0, 1], [0, 1]);
  const captions = [1, 2, 3, 4, 5, 6].map((n) => site(`wash.s${n}`));
  if (device.isTouch) captions[0] = captions[0].replace(/^scroll/i, "Swipe");
  const showCaption = !preview && scene <= last;
  return (
    <div className="absolute inset-0 z-0 overflow-hidden bg-black" data-testid={preview ? "wash-preview-scenes" : "wash-scenes"} data-car={car.id} data-style={style} data-scene={scene}>
      <motion.div className="absolute inset-0" style={{ scale: zoom, transformOrigin: `${ORIGIN.x}% ${ORIGIN.y}%`, willChange: "transform" }}>
        <Layer src={car.dirty} car />
        <CleanReveal src={car.clean} style={style} t={t} />
        <motion.div style={{ opacity: flash }} className="absolute inset-0 bg-white pointer-events-none" />
      </motion.div>
      <Layer src={washImg("swirls")} style={{ opacity: swirlOpacity, rotate: swirlRotate, scale: swirlScale }} />
      <Layer src={washImg("beads")} style={{ opacity: beadOpacity, scale: beadScale }} />
      {last === 5 && <>
        <Layer src={car.clean} car style={{ opacity: keysOpacity, scale: keysScale, filter: "brightness(0.75)" }} />
        <motion.img src={washImg("hand-key")} alt="" draggable={false} data-testid="wash-hand-key"
          style={{ opacity: keysOpacity, y: handY, rotate: handRotate, willChange: "transform, opacity" }}
          className="absolute bottom-0 right-2 sm:right-[14%] h-[46%] sm:h-[68%] w-auto max-w-none pointer-events-none drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" />
      </>}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-black/10 md:from-black/90 md:via-black/35 md:to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/50 pointer-events-none" />
      <motion.div className="absolute inset-0 pointer-events-none" style={{ scale: zoom, transformOrigin: `${ORIGIN.x}% ${ORIGIN.y}%` }}>
        {style === "spotlight" ? <RingFoam t={t} opacity={foamOpacity} />
          : <LineFoam t={t} opacity={foamOpacity} effect={fx} horizontal={style === "squeegee"} diagonal={style === "diagonal"} skew={style === "diagonal" ? skew : 0} />}
      </motion.div>
      <AnimatePresence>{showCaption && <Caption key={scene} text={captions[scene]} testid={`wash-caption-${scene}`} place={place} />}</AnimatePresence>
      {!preview && <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-white/5">
        <motion.div style={{ scaleX: bar, transformOrigin: "left" }} className="h-full w-full bg-sky-500" data-testid="wash-progress" />
      </div>}
    </div>
  );
};
